import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api";

export default function FishingShopPanel({ onClose, refreshEquippedItems }) {
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [state, setState] = useState(null);
  const [activeTab, setActiveTab] = useState("shop");
  const [conversionMethodById, setConversionMethodById] = useState({});
  const [conversionResult, setConversionResult] = useState(null);
  const [fisherToCoinAmount, setFisherToCoinAmount] = useState("");

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const layout = useMemo(() => {
    const isMobile = viewport.width <= 768;
    const panelWidth = isMobile
      ? Math.max(320, viewport.width - 32)
      : Math.max(520, Math.min(viewport.width - 64, 860));

    const panelMaxHeight = Math.max(420, Math.min(Math.floor(viewport.height * 0.9), 820));

    return {
      isMobile,
      panelWidth,
      panelMaxHeight,
    };
  }, [viewport.width, viewport.height]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/v1/island/fishing-shop/state", { withCredentials: true });
      setState(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load fishing shop.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canMeetLevel = (rod) => {
    if (!rod?.required_level_type || !rod?.required_level) return true;
    const lvl = state?.levels?.[rod.required_level_type] || 1;
    return Number(lvl) >= Number(rod.required_level);
  };

  const handleBuyRod = async (rodId) => {
    try {
      await api.post(
        "/v1/island/buy-rod",
        { rod_item_id: rodId },
        { withCredentials: true }
      );
      if (refreshEquippedItems) await refreshEquippedItems();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to buy rod.");
    }
  };

  const handleRepairRod = async (rodId) => {
    try {
      await api.post(
        "/v1/island/repair-rod",
        { rod_item_id: rodId },
        { withCredentials: true }
      );
      if (refreshEquippedItems) await refreshEquippedItems();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to repair rod.");
    }
  };

  const handleBuyHeldItem = async (itemId) => {
    try {
      await api.post(
        "/v1/island/fishing-shop/buy-held-item",
        { item_id: itemId },
        { withCredentials: true }
      );
      if (refreshEquippedItems) await refreshEquippedItems();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to buy item.");
    }
  };

  const formatLevelLabel = (rod) => {
    if (!rod?.required_level_type || !rod?.required_level) return null;
    return `${rod.required_level_type.toUpperCase()} Lv. ${rod.required_level}`;
  };

  const handleConvert = async (conversionId, amountOverride = null) => {
    const conv = (state?.conversions || []).find((c) => c.id === conversionId);
    if (!conv) return;

    const paymentMethod =
      conversionMethodById[conversionId] || (conv.payment_methods || [])[0] || null;

    const payload = { conversion_id: conversionId, payment_method: paymentMethod };
    if (amountOverride !== null && amountOverride !== undefined) {
      payload.amount = amountOverride;
    }

    try {
      const res = await api.post(
        "/v1/island/fishing-shop/convert",
        payload,
        { withCredentials: true }
      );
      if (res?.data?.success) {
        setConversionResult(res.data);
      }
      if (refreshEquippedItems) await refreshEquippedItems();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to convert.");
    }
  };

  const formatMoney = (n) => {
    const val = Number(n || 0);
    if (!Number.isFinite(val)) return String(n);
    return val.toLocaleString();
  };

  const formatNumber = (n) => {
    const val = Number(n);
    if (!Number.isFinite(val)) return String(n ?? "");
    return val.toLocaleString();
  };

  const COIN_ICON = "/items/coin.png";
  const BATTLE_COIN_ICON = "/items/battle_coin.png";
  const MONEY_ICON = "/items/money.png";
  const FISHER_COIN_ICON = state?.fisher_coin_item?.image
    ? `/${state.fisher_coin_item.image}`
    : "/items/fishercoin.png";

  const renderIcon = (src, alt, size = 16) => (
    <img
      src={src}
      alt={alt}
      style={{ width: size, height: size, flexShrink: 0 }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = "/items/pokeball.png";
      }}
    />
  );

  const renderBalancePill = (iconSrc, label, value) => (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        fontSize: 12,
        opacity: 0.95,
      }}
    >
      {renderIcon(iconSrc, label, 16)}
      <span style={{ fontWeight: 700 }}>{formatMoney(value)}</span>
      <span style={{ opacity: 0.85 }}>{label}</span>
    </div>
  );

  return (
    <div
      style={{
        width: layout.panelWidth,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: layout.panelMaxHeight,
        background: "rgba(25, 25, 35, 0.95)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        borderRadius: 12,
        overflow: "hidden",
        color: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: "rgba(0,0,0,0.25)",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>Fishing Shop</div>
          <div style={{ opacity: 0.85, fontSize: 12, marginTop: 4 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <img
                src={state?.fisher_coin_item?.image ? `/${state.fisher_coin_item.image}` : "/items/pokeball.png"}
                alt={state?.fisher_coin_item?.name || "Fisher Coin"}
                style={{ width: 16, height: 16 }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/items/pokeball.png";
                }}
              />
              <span style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
                Fisher Coins: {state?.fisher_coins ?? "—"}
              </span>
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.18)",
        }}
      >
        <button
          onClick={() => setActiveTab("shop")}
          style={{
            background: activeTab === "shop" ? "rgba(255,255,255,0.10)" : "transparent",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "#fff",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Shop
        </button>
        <button
          onClick={() => setActiveTab("conversion")}
          style={{
            background: activeTab === "conversion" ? "rgba(255,255,255,0.10)" : "transparent",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "#fff",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Conversion
        </button>
      </div>

      <div
        style={{
          padding: 14,
          overflowY: "auto",
          maxHeight: layout.panelMaxHeight - 110,
        }}
      >
        {conversionResult && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 11000,
              padding: 16,
              boxSizing: "border-box",
            }}
            onClick={() => setConversionResult(null)}
          >
            <div
              style={{
                width: "min(520px, calc(100vw - 32px))",
                background: "rgba(25, 25, 35, 0.98)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                borderRadius: 12,
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "rgba(0,0,0,0.25)",
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <div style={{ fontWeight: 800 }}>Conversion Complete</div>
                <button
                  onClick={() => setConversionResult(null)}
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>

              <div style={{ padding: 14 }}>
                <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 13 }}>Received</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    borderRadius: 12,
                    background: "rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    marginBottom: 12,
                  }}
                >
                  {conversionResult?.received?.type === "item" &&
                    renderIcon(
                      conversionResult?.received?.image ? `/${conversionResult.received.image}` : "/items/pokeball.png",
                      conversionResult?.received?.name || "Item",
                      28
                    )}
                  {conversionResult?.received?.type === "coins" && renderIcon(COIN_ICON, "Coins", 28)}
                  {conversionResult?.received?.type === "fisher_coins" && renderIcon(FISHER_COIN_ICON, "Fisher Coins", 28)}
                  {conversionResult?.received?.type === "battle_coins" && renderIcon(BATTLE_COIN_ICON, "Battle Coins", 28)}
                  {conversionResult?.received?.type === "money" && renderIcon(MONEY_ICON, "Cash", 28)}
                  <div style={{ fontSize: 13, opacity: 0.95 }}>
                    <span style={{ fontWeight: 800 }}>{conversionResult?.received?.amount}</span>{" "}
                    <span>
                      {conversionResult?.received?.type === "coins"
                        ? "Coins"
                        : conversionResult?.received?.type === "fisher_coins"
                          ? "Fisher Coins"
                          : conversionResult?.received?.type === "battle_coins"
                            ? "Battle Coins"
                            : conversionResult?.received?.type === "money"
                              ? "Cash"
                              : conversionResult?.received?.name || "Item"}
                    </span>
                  </div>
                </div>

                <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 13 }}>Spent</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(conversionResult?.spent || []).map((s, idx) => {
                    const icon =
                      s.type === "coins"
                        ? COIN_ICON
                        : s.type === "battle_coins"
                          ? BATTLE_COIN_ICON
                          : s.type === "money"
                            ? MONEY_ICON
                          : s.type === "item" && s.image
                            ? `/${s.image}`
                            : "/items/pokeball.png";

                    const name =
                      s.type === "coins"
                        ? "Coins"
                        : s.type === "battle_coins"
                          ? "Battle Coins"
                          : s.type === "money"
                            ? "Cash"
                            : s.name || "Item";

                    const amount = s.type === "money" ? `$${formatMoney(s.amount)}` : s.amount;

                    return (
                      <div
                        key={`${s.type}-${idx}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: 10,
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.28)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                      >
                        {renderIcon(icon, name, 22)}
                        <div style={{ fontSize: 12, opacity: 0.95 }}>
                          <span style={{ fontWeight: 800 }}>{amount}</span> <span>{name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div
            style={{
              marginBottom: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(248,113,113,0.18)",
              border: "1px solid rgba(248,113,113,0.35)",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ opacity: 0.9, fontSize: 13 }}>Loading...</div>
        ) : (
          <>
            {activeTab === "shop" && (
              <>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Rods</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
                  {(state?.rods || []).map((rod) => {
                const meetsLevel = canMeetLevel(rod);
                const owned = !!rod.owned;
                const isBroken = owned && Number(rod.durability || 0) <= 0;

                const canBuy = !owned && meetsLevel;
                const canRepair = owned && isBroken && meetsLevel;

                return (
                  <div
                    key={rod.rod_item_id}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      padding: 12,
                      borderRadius: 12,
                      background: "rgba(0,0,0,0.28)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <img
                      src={rod?.image ? `/${rod.image}` : "/items/pokeball.png"}
                      alt={rod?.name || "Rod"}
                      style={{ width: 44, height: 44, flexShrink: 0 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/items/pokeball.png";
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 800, fontSize: 14 }}>{rod?.name || `Rod ${rod.rod_item_id}`}</div>
                        {formatLevelLabel(rod) && (
                          <div
                            style={{
                              fontSize: 12,
                              opacity: 0.85,
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: "1px solid rgba(255,255,255,0.14)",
                              background: meetsLevel ? "rgba(34,197,94,0.12)" : "rgba(248,113,113,0.12)",
                            }}
                          >
                            {formatLevelLabel(rod)}
                          </div>
                        )}
                      </div>
                      {rod?.description && (
                        <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, lineHeight: 1.35 }}>
                          {rod.description}
                        </div>
                      )}
                      <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span style={{ opacity: 0.85 }}>Buy:</span>
                          {rod.currency === "fisher_coins"
                            ? renderIcon(FISHER_COIN_ICON, "Fisher Coins", 16)
                            : renderIcon(COIN_ICON, "Coins", 16)}
                          <span>{formatNumber(rod.buy_cost)}</span>
                          <span style={{ opacity: 0.6 }}>|</span>
                          <span style={{ opacity: 0.85 }}>Repair:</span>
                          {rod.currency === "fisher_coins"
                            ? renderIcon(FISHER_COIN_ICON, "Fisher Coins", 16)
                            : renderIcon(COIN_ICON, "Coins", 16)}
                          <span>{formatNumber(rod.repair_cost)}</span>
                        </span>
                      </div>
                      {owned && (
                        <div style={{ opacity: 0.9, fontSize: 12, marginTop: 4 }}>
                          Durability: {formatNumber(rod.durability)}/{formatNumber(rod.max_durability)}
                        </div>
                      )}
                      {!meetsLevel && (
                        <div style={{ opacity: 0.9, fontSize: 12, marginTop: 4, color: "#fca5a5" }}>
                          Level requirement not met.
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch" }}>
                      {!owned && (
                        <button
                          onClick={() => handleBuyRod(rod.rod_item_id)}
                          disabled={!canBuy}
                          style={{
                            background: canBuy ? "rgba(59,130,246,0.85)" : "rgba(255,255,255,0.10)",
                            border: "1px solid rgba(255,255,255,0.14)",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "8px 12px",
                            cursor: canBuy ? "pointer" : "not-allowed",
                            minWidth: 92,
                          }}
                        >
                          Buy
                        </button>
                      )}
                      {owned && isBroken && (
                        <button
                          onClick={() => handleRepairRod(rod.rod_item_id)}
                          disabled={!canRepair}
                          style={{
                            background: canRepair ? "rgba(34,197,94,0.80)" : "rgba(255,255,255,0.10)",
                            border: "1px solid rgba(255,255,255,0.14)",
                            color: "#fff",
                            borderRadius: 10,
                            padding: "8px 12px",
                            cursor: canRepair ? "pointer" : "not-allowed",
                            minWidth: 92,
                          }}
                        >
                          Repair
                        </button>
                      )}
                      {owned && !isBroken && (
                        <div style={{ opacity: 0.9, fontSize: 12, textAlign: "right" }}>Owned</div>
                      )}
                    </div>
                  </div>
                );
                  })}
                </div>

                <div style={{ fontWeight: 800, marginBottom: 8 }}>Held Items</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(state?.held_items || []).map((it) => {
                const fisherCoinsOk = Number(state?.fisher_coins || 0) >= Number(it.fisher_coins || 0);
                const reqOk = (it.requirements || []).every((r) => Number(r.owned_amount || 0) >= Number(r.amount || 0));
                const canBuy = fisherCoinsOk && reqOk;

                return (
                  <div
                    key={it.item_id}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "center",
                      padding: 12,
                      borderRadius: 12,
                      background: "rgba(0,0,0,0.28)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <img
                      src={it?.image ? `/${it.image}` : "/items/pokeball.png"}
                      alt={it?.name || "Item"}
                      style={{ width: 44, height: 44, flexShrink: 0 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/items/pokeball.png";
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 14 }}>{it?.name || `Item ${it.item_id}`}</div>
                      {it?.description && (
                        <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, lineHeight: 1.35 }}>
                          {it.description}
                        </div>
                      )}
                      <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span>Cost:</span>
                          <img
                            src={state?.fisher_coin_item?.image ? `/${state.fisher_coin_item.image}` : "/items/pokeball.png"}
                            alt={state?.fisher_coin_item?.name || "Fisher Coin"}
                            style={{ width: 16, height: 16, flexShrink: 0 }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/items/pokeball.png";
                            }}
                          />
                          <span>{formatNumber(it.fisher_coins)} Fisher Coins</span>
                        </span>
                      </div>
                      {(it.requirements || []).length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                          {(it.requirements || []).map((r) => {
                            const ok = Number(r.owned_amount || 0) >= Number(r.amount || 0);
                            return (
                              <div key={r.item_id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
                                <img
                                  src={r?.image ? `/${r.image}` : "/items/pokeball.png"}
                                  alt={r?.name || "Req"}
                                  style={{ width: 18, height: 18, flexShrink: 0, opacity: ok ? 1 : 0.6 }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/items/pokeball.png";
                                  }}
                                />
                                <span style={{ opacity: ok ? 0.95 : 0.75 }}>
                                  {formatNumber(r.amount)}× {r.name || r.item_id} ({formatNumber(r.owned_amount || 0)} owned)
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                      <button
                        onClick={() => handleBuyHeldItem(it.item_id)}
                        disabled={!canBuy}
                        style={{
                          background: canBuy ? "rgba(168,85,247,0.78)" : "rgba(255,255,255,0.10)",
                          border: "1px solid rgba(255,255,255,0.14)",
                          color: "#fff",
                          borderRadius: 10,
                          padding: "8px 12px",
                          cursor: canBuy ? "pointer" : "not-allowed",
                          minWidth: 92,
                        }}
                      >
                        Buy
                      </button>
                      <div style={{ opacity: 0.85, fontSize: 12 }}>Owned: {formatNumber(it.owned_amount || 0)}</div>
                    </div>
                  </div>
                );
                  })}
                </div>
              </>
            )}

            {activeTab === "conversion" && (
              <>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Balances</div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  {renderBalancePill(COIN_ICON, "Coins", state?.balances?.coins || 0)}
                  {renderBalancePill(BATTLE_COIN_ICON, "Battle Coins", state?.balances?.battle_coins || 0)}
                  {renderBalancePill(FISHER_COIN_ICON, "Fisher Coins", state?.fisher_coins || 0)}
                  {renderBalancePill(MONEY_ICON, "Cash", state?.balances?.money || 0)}
                </div>

                <div style={{ fontWeight: 800, marginBottom: 8 }}>Conversions</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(state?.conversions || []).map((conv) => {
                    const methods = conv.payment_methods || [];
                    const selectedMethod =
                      conversionMethodById[conv.id] || methods[0] || null;

                    const baseFromAmt = Number(conv?.from?.amount || 0);
                    const baseFromAmtRaw = Number(conv?.base_from_amount ?? baseFromAmt);
                    const baseToAmt = Number(conv?.to?.coins || 0);
                    const rawFisherToCoin = fisherToCoinAmount === "" ? baseFromAmt : Number(fisherToCoinAmount);
                    const fisherMult = baseFromAmt > 0 ? Math.floor(rawFisherToCoin / baseFromAmt) : 0;
                    const fisherSpend = fisherMult * baseFromAmt;
                    const fisherReceive = fisherMult * baseToAmt;

                    const basePayouts = conv?.base_payouts || {};

                    const methodIcon = (m) => {
                      if (m === "fisher_coins") return FISHER_COIN_ICON;
                      if (m === "coins") return COIN_ICON;
                      if (m === "battle_coins") return BATTLE_COIN_ICON;
                      if (m === "money") return MONEY_ICON;
                      if (m === "random_gem") {
                        const g = (conv.random_gem_items || [])[0];
                        return g?.image ? `/${g.image}` : "/items/pokeball.png";
                      }
                      return "/items/pokeball.png";
                    };

                    const methodLabel = (m) => {
                      if (m === "fisher_coins") return "Fisher Coins";
                      if (m === "coins") return "Coins";
                      if (m === "battle_coins") return "Battle Coins";
                      if (m === "money") return "Cash";
                      if (m === "random_gem") return "Random Gem";
                      return m;
                    };

                    const canPay = (() => {
                      if (conv.type === "currency") {
                        return fisherMult > 0 && Number(state?.fisher_coins || 0) >= fisherSpend;
                      }

                      return Number(conv.from_item?.owned_amount || 0) >= Number(conv.from_amount || 1);
                    })();

                    const displayTitle = (() => {
                      if (conv.type === "currency") return "Fisher Coins → Coins";
                      return `${conv.from_amount || 1}× ${conv.from_item?.name || conv.from_item_id}`;
                    })();

                    return (
                      <div
                        key={conv.id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          padding: 12,
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.28)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                      >
                        <img
                          src={(() => {
                            if (conv.type === "currency") return FISHER_COIN_ICON;
                            if (selectedMethod === "random_gem") {
                              const g = (conv.random_gem_items || [])[0];
                              return g?.image ? `/${g.image}` : "/items/pokeball.png";
                            }
                            return conv.from_item?.image ? `/${conv.from_item.image}` : "/items/pokeball.png";
                          })()}
                          alt={(() => {
                            if (conv.type === "currency") return "Fisher Coins";
                            if (selectedMethod === "random_gem") return "Gem";
                            return conv.from_item?.name || "Conversion";
                          })()}
                          style={{ width: 44, height: 44, flexShrink: 0 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/items/pokeball.png";
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 14 }}>{displayTitle}</div>
                          {conv.type === "item" && (
                            <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                              Owned: {formatMoney(conv.from_item?.owned_amount || 0)}
                            </div>
                          )}

                          {conv.type === "currency" ? (
                            <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  flexWrap: "wrap",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderIcon(
                                  FISHER_COIN_ICON,
                                  state?.fisher_coin_item?.name || "Fisher Coin",
                                  16
                                )}
                                <input
                                  value={fisherToCoinAmount}
                                  placeholder={String(baseFromAmt || "")}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "") {
                                      setFisherToCoinAmount("");
                                      return;
                                    }
                                    const num = Number(v);
                                    if (!Number.isFinite(num) || num < 0) return;
                                    setFisherToCoinAmount(v);
                                  }}
                                  style={{
                                    width: 90,
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.14)",
                                    color: "#fff",
                                    borderRadius: 8,
                                    padding: "4px 8px",
                                    fontSize: 12,
                                    outline: "none",
                                  }}
                                  inputMode="numeric"
                                />
                                <span style={{ opacity: 0.9 }}>(owned {formatNumber(state?.fisher_coins || 0)})</span>
                                <span style={{ opacity: 0.7, padding: "0 2px" }}>→</span>
                                {renderIcon(COIN_ICON, "Coins", 16)}
                                <span style={{ overflowWrap: "anywhere" }}>{formatNumber(fisherReceive)} Coins</span>
                              </span>
                              {baseFromAmtRaw !== baseFromAmt && baseFromAmtRaw > 0 && (
                                <div style={{ marginTop: 6, opacity: 0.85 }}>
                                  Rate: {formatNumber(baseFromAmt)} Fisher Coins per 1 Coin (was {formatNumber(baseFromAmtRaw)})
                                </div>
                              )}
                              {fisherMult > 0 && (
                                <div style={{ marginTop: 6, opacity: 0.9, fontSize: 12 }}>
                                  Spending {formatNumber(fisherSpend)} Fisher Coins for {formatNumber(fisherReceive)} Coins
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                                Payout:
                              </div>
                              {methods.length > 1 ? (
                                <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                  {methods.map((m) => (
                                    <button
                                      key={m}
                                      onClick={() =>
                                        setConversionMethodById((prev) => ({ ...prev, [conv.id]: m }))
                                      }
                                      style={{
                                        background: (conversionMethodById[conv.id] || methods[0]) === m ? "rgba(255,255,255,0.10)" : "transparent",
                                        border: "1px solid rgba(255,255,255,0.14)",
                                        color: "#fff",
                                        borderRadius: 10,
                                        padding: "6px 10px",
                                        cursor: "pointer",
                                        fontSize: 12,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}
                                    >
                                      {renderIcon(methodIcon(m), methodLabel(m), 14)}
                                      <span>{methodLabel(m)}</span>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6 }}>
                                  {renderIcon(methodIcon(methods[0]), methodLabel(methods[0]), 14)}
                                  <span>{methodLabel(methods[0])}</span>
                                </div>
                              )}

                              <div style={{ marginTop: 6, opacity: 0.9, fontSize: 12 }}>
                                {selectedMethod === "fisher_coins" && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    {renderIcon(FISHER_COIN_ICON, "Fisher Coins", 16)}
                                    <span>
                                      {formatNumber(conv.payouts?.fisher_coins?.amount)} Fisher Coins
                                      {basePayouts?.fisher_coins?.amount &&
                                        Number(basePayouts.fisher_coins.amount) !== Number(conv.payouts?.fisher_coins?.amount) && (
                                          <span style={{ marginLeft: 6, opacity: 0.75 }}>
                                            (was {formatNumber(basePayouts.fisher_coins.amount)})
                                          </span>
                                        )}
                                    </span>
                                  </span>
                                )}
                                {selectedMethod === "coins" && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    {renderIcon(COIN_ICON, "Coins", 16)}
                                    <span>
                                      {formatNumber(conv.payouts?.coins?.amount)} Coins
                                      {basePayouts?.coins?.amount &&
                                        Number(basePayouts.coins.amount) !== Number(conv.payouts?.coins?.amount) && (
                                          <span style={{ marginLeft: 6, opacity: 0.75 }}>
                                            (was {formatNumber(basePayouts.coins.amount)})
                                          </span>
                                        )}
                                    </span>
                                  </span>
                                )}
                                {selectedMethod === "battle_coins" && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    {renderIcon(BATTLE_COIN_ICON, "Battle Coins", 16)}
                                    <span>
                                      {formatNumber(conv.payouts?.battle_coins?.amount)} Battle Coins
                                      {basePayouts?.battle_coins?.amount &&
                                        Number(basePayouts.battle_coins.amount) !== Number(conv.payouts?.battle_coins?.amount) && (
                                          <span style={{ marginLeft: 6, opacity: 0.75 }}>
                                            (was {formatNumber(basePayouts.battle_coins.amount)})
                                          </span>
                                        )}
                                    </span>
                                  </span>
                                )}
                                {selectedMethod === "money" && (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                    {renderIcon(MONEY_ICON, "Cash", 16)}
                                    <span>
                                      ${formatMoney(conv.payouts?.money?.amount)} Cash
                                      {basePayouts?.money?.amount &&
                                        Number(basePayouts.money.amount) !== Number(conv.payouts?.money?.amount) && (
                                          <span style={{ marginLeft: 6, opacity: 0.75 }}>
                                            (was ${formatMoney(basePayouts.money.amount)})
                                          </span>
                                        )}
                                    </span>
                                  </span>
                                )}
                                {selectedMethod === "random_gem" && (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <div style={{ opacity: 0.9 }}>
                                      Random gem x{conv.payouts?.random_gem?.amount_min}-{conv.payouts?.random_gem?.amount_max}
                                      {basePayouts?.random_gem?.amount_min && basePayouts?.random_gem?.amount_max &&
                                        (Number(basePayouts.random_gem.amount_min) !== Number(conv.payouts?.random_gem?.amount_min) ||
                                          Number(basePayouts.random_gem.amount_max) !== Number(conv.payouts?.random_gem?.amount_max)) && (
                                          <span style={{ marginLeft: 6, opacity: 0.75 }}>
                                            (was x{basePayouts.random_gem.amount_min}-{basePayouts.random_gem.amount_max})
                                          </span>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                      {(conv.random_gem_items || []).map((g) => (
                                        <div
                                          key={g.item_id}
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "4px 8px",
                                            borderRadius: 999,
                                            border: "1px solid rgba(255,255,255,0.12)",
                                            background: "rgba(255,255,255,0.06)",
                                          }}
                                        >
                                          {renderIcon(g.image ? `/${g.image}` : "/items/pokeball.png", g.name || "Gem", 16)}
                                          <span style={{ fontWeight: 800 }}>
                                            +{conv.payouts?.random_gem?.amount_min}-{conv.payouts?.random_gem?.amount_max}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                          <button
                            onClick={() =>
                              handleConvert(
                                conv.id,
                                conv.type === "currency" ? fisherSpend : null
                              )
                            }
                            disabled={!canPay}
                            style={{
                              background: canPay ? "rgba(34,197,94,0.80)" : "rgba(255,255,255,0.10)",
                              border: "1px solid rgba(255,255,255,0.14)",
                              color: "#fff",
                              borderRadius: 10,
                              padding: "8px 12px",
                              cursor: canPay ? "pointer" : "not-allowed",
                              minWidth: 92,
                            }}
                          >
                            Convert
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
